import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  isAuthenticated as checkIsAuthenticated,
  getAccessToken,
  getRefreshToken,
  clearAuthTokens,
  setAuthTokens,
} from '../utils/auth';

const API_URL = import.meta.env.VITE_API_URL;

interface AuthContextType {
  isAuthenticated: boolean;
  logout: () => void;
  getAuthHeaders: () => { Authorization: string } | {};
  refreshTokens: () => Promise<boolean>;
  refreshAuthState: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(checkIsAuthenticated());

  useEffect(() => {
    setIsAuthenticated(checkIsAuthenticated());
  }, []);

  const refreshAuthState = () => {
    setIsAuthenticated(checkIsAuthenticated());
  };

  const logout = () => {
    clearAuthTokens();
    setIsAuthenticated(false);
  };

  const getAuthHeaders = () => {
    const accessToken = getAccessToken();
    if (!accessToken) return {};

    return {
      Authorization: `Bearer ${accessToken}`,
    };
  };

  const refreshTokens = async (): Promise<boolean> => {
    const refreshToken = getRefreshToken();

    if (!refreshToken) {
      logout();
      return false;
    }

    try {
      const response = await fetch(`${API_URL}/auth/refreshToken`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refreshToken,
          isHeimdall: true,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to refresh token');
      }

      const data = await response.json();

      setAuthTokens({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
      });

      return true;
    } catch {
      logout();
      return false;
    }
  };

  const value: AuthContextType = {
    isAuthenticated,
    logout,
    getAuthHeaders,
    refreshTokens,
    refreshAuthState,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
